import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Phone, Mail, ShieldCheck, HelpCircle } from 'lucide-react'

export default function ServiceDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [service, setService] = useState(null)
  const [allServices, setAllServices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchServiceData = async () => {
      setLoading(true)
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/services')
        if (res.ok) {
          const data = await res.json()
          setAllServices(data)
          
          // Find the service that matches the current slug
          const foundService = data.find(s => s.slug?.da === slug)
          if (foundService) {
            setService(foundService)
          } else {
            setService(null)
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af ydelse:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchServiceData()
  }, [slug])

  if (loading) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-black border-t-transparent" />
      </div>
    )
  }

  if (!service) {
    return (
      <div className="bg-slate-50 min-h-screen py-20">
        <div className="mx-auto max-w-md px-4 text-center space-y-6">
          <HelpCircle className="mx-auto h-16 w-16 text-slate-400" />
          <h1 className="text-2xl font-semibold text-slate-900">Ydelsen blev ikke fundet</h1>
          <p className="text-slate-600">
            Vi kunne desværre ikke finde den ydelse, du leder efter. Den er muligvis blevet flyttet eller slettet.
          </p>
          <div className="pt-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full bg-black px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Gå til forsiden
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Filter out the current service to show other services in the sidebar
  const otherServices = allServices.filter(s => s.id !== service.id)

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Clean Page Header (No dark background) */}
      <section className="bg-white pt-16 pb-8 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start gap-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-black transition-colors"
            >
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Tilbage til ydelser
            </Link>
            <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-900">
              {service.title?.da}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            
            {/* Left Column: Service Details (2 cols) */}
            <div className="lg:col-span-2 space-y-8">
              {/* Image */}
              <div className="aspect-video w-full overflow-hidden rounded-2xl bg-slate-100 shadow-sm border border-slate-200">
                <img
                  src={service.image?.[0]}
                  alt={service.title?.da}
                  className="h-full w-full object-cover object-center"
                />
              </div>

              {/* Description & Long Description */}
              <div className="space-y-6">
                <p className="text-xl text-slate-800 font-medium leading-relaxed border-l-4 border-black pl-4">
                  {service.description?.da}
                </p>

                <div
                  className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4 text-sm sm:text-base
                    prose-ul:list-disc prose-ul:pl-5 prose-li:my-1 prose-strong:text-slate-900 prose-strong:font-semibold"
                  dangerouslySetInnerHTML={{ __html: service.long_description?.da }}
                />
              </div>

              {/* Trust Badge / Info */}
              <div className="rounded-2xl bg-white border border-slate-200 p-6 flex items-start gap-4 shadow-sm">
                <ShieldCheck className="h-8 w-8 text-black shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Vores kvalitetsgaranti</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Når du vælger Probyg Maler til din maleropgave, er du sikret et professionelt resultat af højeste kvalitet. Vi forlader ikke opgaven, før du er fuldt ud tilfreds med det udførte arbejde.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar (1 col) */}
            <div className="space-y-8">
              
              {/* Quick Contact / CTA Card */}
              <div className="rounded-2xl bg-slate-900 p-8 text-white space-y-6 shadow-md">
                <h3 className="font-semibold text-xl">Få et uforpligtende tilbud</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Går du med et projekt i tankerne? Udfyld vores formular eller kontakt os direkte for et uforpligtende tilbud på <strong>{service.title?.da.toLowerCase()}</strong>.
                </p>
                
                <div className="space-y-3 pt-2">
                  <Link
                    to="/kontakt"
                    state={{ service: service.title?.da }}
                    className="inline-flex w-full items-center justify-center rounded-full bg-white py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors shadow-sm"
                  >
                    Få gratis tilbud nu
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  
                  <a
                    href="tel:+4592900091"
                    className="inline-flex w-full items-center justify-center rounded-full border border-slate-700 bg-slate-800/50 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Ring: +45 92 90 00 91
                  </a>
                </div>
              </div>

              {/* Other Services List */}
              {otherServices.length > 0 && (
                <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm space-y-4">
                  <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Andre ydelser</h3>
                  <div className="divide-y divide-slate-100">
                    {otherServices.map((s) => (
                      <Link
                        key={s.id}
                        to={`/ydelser/${s.slug?.da}`}
                        className="block py-3 group hover:text-black transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700 group-hover:text-black transition-colors">
                            {s.title?.da}
                          </span>
                          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-black group-hover:translate-x-1 transition-all" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
    </div>
  )
}
